import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import styled from "styled-components";
import { humanize, isSecretKey } from "./providersData";
import { Chip, Chips, Mono, Muted } from "./providersUi";

const isPlainObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const isPrimitive = (value: unknown): value is string | number | boolean | null | undefined => value === null || value === undefined || ["string", "number", "boolean"].includes(typeof value);

/** Replaces secret values, so a raw dump never shows them. */
export const maskSecrets = (value: unknown): unknown => {
	if (Array.isArray(value)) return value.map(maskSecrets);
	if (!isPlainObject(value)) return value;
	return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, isSecretKey(key) && isPrimitive(item) && item !== "" && item != null ? "••••••" : maskSecrets(item)]));
};

const Tree = styled.dl`
	display: grid;
	grid-template-columns: minmax(120px, 200px) minmax(0, 1fr);
	align-items: baseline;
	gap: 8px 16px;
	margin: 0;
	dt {
		color: #667085;
		font-size: 12px;
		overflow-wrap: anywhere;
	}
	dd {
		min-width: 0;
		margin: 0;
		color: #182230;
		overflow-wrap: anywhere;
	}
	dd.group {
		grid-column: 1 / -1;
		padding: 10px 0 2px 12px;
		border-left: 2px solid #eaecf0;
	}
	.group-title {
		margin-bottom: 8px;
		color: #344054;
		font-size: 12px;
		font-weight: 600;
	}
	.bool-yes {
		color: #067647;
		font-weight: 500;
	}
	.bool-no {
		color: #667085;
	}
	.secret {
		display: inline-flex;
		align-items: center;
		gap: 6px;
	}
	.secret button {
		display: inline-grid;
		place-items: center;
		width: 22px;
		height: 22px;
		padding: 0;
		border: 0;
		border-radius: 5px;
		background: transparent;
		color: #98a2b3;
		cursor: pointer;
	}
	.secret button:hover {
		background: #f2f4f7;
		color: #475467;
	}
	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 12px;
	}
	th,
	td {
		padding: 6px 10px;
		border-bottom: 1px solid #eaecf0;
		text-align: left;
		vertical-align: top;
	}
	th {
		background: #f9fafb;
		color: #667085;
		font-weight: 500;
		white-space: nowrap;
	}
	tr:last-child td {
		border-bottom: 0;
	}
	.table-wrap {
		overflow-x: auto;
		border: 1px solid #eaecf0;
		border-radius: 8px;
	}
	@media (max-width: 600px) {
		grid-template-columns: minmax(0, 1fr);
		gap: 2px;
		dd + dt {
			margin-top: 8px;
		}
	}
`;

const Secret = ({ value }: { value: string }) => {
	useLocale();
	const [shown, setShown] = useState(false);
	return (
		<span className="secret">
			<Mono>{shown ? value : "••••••••"}</Mono>
			<button type="button" aria-label={shown ? msg("hideValue") : msg("showValue")} title={shown ? msg("hideValue") : msg("showValue")} onClick={() => setShown(!shown)}>
				{shown ? <EyeOff size={13} /> : <Eye size={13} />}
			</button>
		</span>
	);
};

const PrimitiveValue = ({ name, value }: { name: string; value: unknown }) => {
	useLocale();
	if (value === null || value === undefined || value === "") return <Muted>—</Muted>;
	if (typeof value === "boolean") return <span className={value ? "bool-yes" : "bool-no"}>{value ? msg("yes") : msg("no")}</span>;
	if (isSecretKey(name)) return <Secret value={String(value)} />;
	if (typeof value === "number") return <Mono>{value}</Mono>;
	return <>{String(value)}</>;
};

const columnsOf = (items: Record<string, unknown>[]) => {
	const keys: string[] = [];
	for (const item of items) for (const key of Object.keys(item)) if (!keys.includes(key)) keys.push(key);
	return keys;
};

const ArrayValue = ({ name, items }: { name: string; items: unknown[] }) => {
	useLocale();
	if (!items.length) return <Muted>{msg("none")}</Muted>;
	if (items.every(isPrimitive))
		return (
			<Chips>
				{items.map((item, index) => (
					<Chip key={index}>{item === null || item === undefined || item === "" ? "—" : String(item)}</Chip>
				))}
			</Chips>
		);
	const rows = items.filter(isPlainObject);
	const flat = rows.length === items.length && rows.every(row => Object.values(row).every(isPrimitive));
	if (flat) {
		const columns = columnsOf(rows);
		return (
			<div className="table-wrap">
				<table>
					<thead>
						<tr>
							{columns.map(column => (
								<th key={column}>{humanize(column)}</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr key={index}>
								{columns.map(column => (
									<td key={column}>
										<PrimitiveValue name={column} value={row[column]} />
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}
	return (
		<div>
			{items.map((item, index) => (
				<ValueTree key={index} value={{ [`${humanize(name)} ${index + 1}`]: item }} />
			))}
		</div>
	);
};

/** Any settings object read as labelled rows; nested groups indent, lists become chips or small tables. */
export const ValueTree = ({ value }: { value: Record<string, unknown> }) => {
	useLocale();
	const entries = Object.entries(value).filter(([key]) => !key.startsWith("$__"));
	if (!entries.length) return <Muted>{msg("nothingConfigured")}</Muted>;
	return (
		<Tree>
			{entries.map(([key, item]) =>
				isPlainObject(item) ? (
					<dd key={key} className="group">
						<div className="group-title">{humanize(key)}</div>
						<ValueTree value={item} />
					</dd>
				) : (
					<Entry key={key} name={key} value={item} />
				),
			)}
		</Tree>
	);
};

const Entry = ({ name, value }: { name: string; value: unknown }) => { useLocale(); return ((
	<>
		<dt>{humanize(name)}</dt>
		<dd>{Array.isArray(value) ? <ArrayValue name={name} items={value} /> : <PrimitiveValue name={name} value={value} />}</dd>
	</>
)); };
